import snowyMountains from "@/assets/themes/snowy-mountains.png";
import liquidGold from "@/assets/themes/liquid-gold.jpeg";
import inkExplosion from "@/assets/themes/ink-explosion.jpeg";
import paintSplash from "@/assets/themes/paint-splash.jpeg";
import smokeArt from "@/assets/themes/smoke-art.jpeg";
import neonDream from "@/assets/themes/neon-dream.jpeg";
import fireAndIce from "@/assets/themes/fire-and-ice.jpeg";
import crystalShatter from "@/assets/themes/crystal-shatter.jpeg";
import watercolorStorm from "@/assets/themes/watercolor-storm.jpeg";
import goldenMandala from "@/assets/themes/golden-mandala.jpeg";
import cosmicDust from "@/assets/themes/cosmic-dust.jpeg";
import dubaiGold from "@/assets/themes/dubai-gold.jpeg";
import parisRomance from "@/assets/themes/paris-romance.jpeg";
import jungleWaterfall from "@/assets/themes/jungle-waterfall.jpeg";
import nyNights from "@/assets/themes/ny-nights.jpeg";
import tropicalForest from "@/assets/themes/tropical-forest.jpeg";
import northernLights from "@/assets/themes/northern-lights.jpeg";
import desertDunes from "@/assets/themes/desert-dunes.jpeg";
import oceanBeach from "@/assets/themes/ocean-beach.jpeg";

export type ChatThemeCategory = "free" | "premium" | "animated" | "seasonal";
export type ChatThemeEffect = "none";

export interface ChatTheme {
  id: string;
  name: string;
  category: ChatThemeCategory;
  price: number;
  /** Plain CSS background (for free midnight) */
  background: string;
  /** Optional image url to use as full-bleed background */
  image?: string;
  /** Dark overlay opacity 0-1 applied above the image for legibility */
  overlay?: number;
  effect: ChatThemeEffect;
  tagline: string;
  accent: string;
}

const imageTheme = (id: string, name: string, image: string, tagline: string, accent: string): ChatTheme => ({
  id,
  name,
  category: "premium",
  price: 50,
  background: "#000",
  image,
  overlay: 0.2,
  effect: "none",
  tagline,
  accent,
});

export const CHAT_THEMES: ChatTheme[] = [
  {
    id: "midnight",
    name: "Midnight",
    category: "free",
    price: 0,
    background: "#000000",
    effect: "none",
    tagline: "Default · pure black",
    accent: "#7c5cff",
  },
  imageTheme("snowy-mountains", "Snowy Mountains", snowyMountains, "Moonlit alpine peaks", "#bde0fe"),
  imageTheme("liquid-gold", "Liquid Gold", liquidGold, "Black & gold flow", "#c9a84c"),
  imageTheme("ink-explosion", "Ink Explosion", inkExplosion, "Blue ink in water", "#5cbdb9"),
  imageTheme("paint-splash", "Paint Splash", paintSplash, "Pink & purple burst", "#e94560"),
  imageTheme("smoke-art", "Smoke Art", smokeArt, "Blue & gold swirls", "#a78bfa"),
  imageTheme("neon-dream", "Neon Dream", neonDream, "Purple & cyan fluid", "#67e8f9"),
  imageTheme("fire-and-ice", "Fire and Ice", fireAndIce, "Fire meets ice", "#ff6b35"),
  imageTheme("crystal-shatter", "Crystal Shatter", crystalShatter, "Shattered blue glass", "#7ec8e3"),
  imageTheme("watercolor-storm", "Watercolor Storm", watercolorStorm, "Dark navy watercolor", "#475569"),
  imageTheme("golden-mandala", "Golden Mandala", goldenMandala, "Gold on deep blue", "#c9a84c"),
  imageTheme("cosmic-dust", "Cosmic Dust", cosmicDust, "Purple & gold galaxy", "#a78bfa"),
  imageTheme("dubai-gold", "Dubai Gold", dubaiGold, "Golden skyline sunset", "#f59e0b"),
  imageTheme("paris-romance", "Paris Romance", parisRomance, "Eiffel Tower bokeh", "#fbbf24"),
  imageTheme("jungle-waterfall", "Jungle Waterfall", jungleWaterfall, "Misty tropical falls", "#34d399"),
  imageTheme("ny-nights", "New York Nights", nyNights, "NYC skyline by night", "#94a3b8"),
  imageTheme("tropical-forest", "Tropical Forest", tropicalForest, "Sunlit jungle canopy", "#65a30d"),
  imageTheme("northern-lights", "Northern Lights", northernLights, "Vivid green aurora", "#4ade80"),
  imageTheme("desert-dunes", "Desert Dunes", desertDunes, "Sunset over dunes", "#ea580c"),
  imageTheme("ocean-beach", "Ocean Beach", oceanBeach, "Tropical sunset waves", "#fb923c"),
];

export const DEFAULT_THEME_ID = "midnight";
export const FREE_THEME_IDS = CHAT_THEMES.filter((t) => t.price === 0).map((t) => t.id);

export const getTheme = (id: string | null | undefined): ChatTheme =>
  CHAT_THEMES.find((t) => t.id === id) ?? CHAT_THEMES[0];
